import { ButtonPrimary } from "@nova/component-btn"
import { AlertContainer } from "@nova/component-alert-container"
import { createQuery } from "@nova/queryable"
import { useBatchPilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import {
    closeChecklistCreationModal,
    openChecklistCreationModal,
    selectChecklistCreationModalOpen,
} from "features/pilot-tracking/checklists/slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import * as Icons from "@nova/component-icons"
import { useRouter } from "pages"
import { getGenericWorkspacePage, getGenericWorkspacePageHeader } from "../WorkspaceRoutes"
import { CreateChecklistModal } from "./CreateChecklistModal"
import { Status } from "@nova/ds-component-status"
import { selectDefaultChecklistId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { setChecklistFilter } from "features/pilot-tracking/redux/missions-table-slice"
import { useState } from "react"

export const ChecklistsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "Checklists",
        rightItems: [
            () => {
                const dispatch = useAppDispatch()
                const modalOpen = useAppSelector(selectChecklistCreationModalOpen)
                const { isEditor } = useWorkspacePermissions()

                if (!isEditor) return null

                return (
                    <>
                        {modalOpen && (
                            <CreateChecklistModal
                                onClose={() => dispatch(closeChecklistCreationModal())}
                            />
                        )}
                        <ButtonPrimary
                            title="New Checklist"
                            LeftIcon={Icons.Plus}
                            onClick={() => dispatch(openChecklistCreationModal())}
                        />
                    </>
                )
            },
        ],
    }),
    Content: ChecklistsPageContent,
    maxWidth: 1200,
})

const CHECKLIST_GRID_COLS = "1fr 90px 90px"

const checklistsQuery = createQuery({
    resource: "checklists",
    select: ["id", "name", "org_id", "description"],
    aggregates: [
        { field: "versions.form_length", func: "MAX", alias: "form_length" },
        { field: "records.id", func: "COUNT", alias: "numSubmissions" },
    ],
})

const missionsWithoutChecklistQuery = createQuery({
    resource: "missions",
    aggregates: [{ field: "id", func: "COUNT", alias: "count" }],
    filters: [{ field: "checklist_links.mission_id", op: "IS NULL", value: null }],
})

const BANNER_KEY = "missing-checklist-banner"

function ChecklistsPageContent() {
    const dispatch = useAppDispatch()

    const [hasDismissedBanner, setHasDismissedBanner] = useState(
        localStorage.getItem(BANNER_KEY) === "true"
    )

    const { data: batchData, isLoading } = useBatchPilotTrackingQuery({
        checklists: checklistsQuery,
        missingChecklist: missionsWithoutChecklistQuery,
    })
    const data = batchData?.checklists
    const missingCount = batchData?.missingChecklist?.[0]?.count ?? 0
    const { isEditor } = useWorkspacePermissions()

    const router = useRouter("/workspace/:workspaceId")
    const defaultChecklistId = useAppSelector(selectDefaultChecklistId)

    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    if (!data?.length) {
        return (
            <div className="flex flex-col items-center gap-2">
                <h1 className="text-white">Add a Checklist</h1>
                <h5 className="text-gray-100">
                    Create checklists for your Pilot in Command to complete during operations.
                </h5>
                {isEditor && (
                    <ButtonPrimary
                        className="mt-4"
                        title="New Checklist"
                        LeftIcon={Icons.Plus}
                        onClick={() => dispatch(openChecklistCreationModal())}
                    />
                )}
            </div>
        )
    }

    const handleReviewClick = () => {
        dispatch(setChecklistFilter(true))
    }

    const handleDismissClick = () => {
        setHasDismissedBanner(true)
        localStorage.setItem(BANNER_KEY, "true")
    }

    return (
        <div className="flex flex-col gap-4">
            {!!missingCount && !hasDismissedBanner && (
                <AlertContainer
                    type="warning"
                    variant="banner"
                    title={`${missingCount} Flights missing a checklist`}
                    action={
                        <>
                            <div className="text-gray-100">•</div>
                            <router.Link
                                to={{ path: "/fleet/flights" }}
                                onClick={handleReviewClick}
                                className="flex items-center gap-1.5 text-white"
                            >
                                Review <Icons.SmallIcon Icon={Icons.ArrowRight} />
                            </router.Link>
                        </>
                    }
                    onClose={handleDismissClick}
                />
            )}
            <div className="w-full mt-2 flex flex-col gap-2">
                <div
                    className="grid gap-x-8 px-6 pb-3"
                    style={{ gridTemplateColumns: CHECKLIST_GRID_COLS }}
                >
                    <small className="font-mono text-gray-400">Name</small>
                    <small className="font-mono text-gray-400 justify-self-end">Questions</small>
                    <small className="font-mono text-gray-400 justify-self-end">Submissions</small>
                </div>

                {data.map((checklist) => (
                    <router.Link
                        to={{
                            path: "/fleet/checklists/:checklistId",
                            params: { checklistId: checklist.id },
                        }}
                        key={checklist.id}
                        className="grid items-center gap-x-8 p-6 border border-white-100 rounded-xl ring-inset hover:ring-2 hover:ring-white-200 hover:bg-white-100"
                        style={{ gridTemplateColumns: CHECKLIST_GRID_COLS }}
                    >
                        <div className="flex items-center gap-4 col-span-1">
                            {defaultChecklistId === checklist.id && (
                                <Status type="positive" variant="outline">
                                    Default
                                </Status>
                            )}
                            <div className="flex flex-col gap-1">
                                <h3 className="telegraf">{checklist.name}</h3>
                                <small className="text-gray-400 font-mono">
                                    {checklist.description}
                                </small>
                            </div>
                        </div>

                        {/* Force the numbers to align to the right edge of their grid column */}
                        <h3 className="text-gray-100 justify-self-end">{checklist.form_length}</h3>
                        <h3 className="text-gray-100 justify-self-end">
                            {checklist.numSubmissions}
                        </h3>
                    </router.Link>
                ))}
            </div>
        </div>
    )
}
